import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { ArrowLeftRight, ChevronLeft, ChevronRight, Package, AlertCircle, Plus, X, Info, Check, Download, Upload, Loader2, FileSpreadsheet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pro017List } from "../create-pro017-form";
import { useExcelImport } from "../shared/use-excel-import";
import { ExcelUploadPreview } from "../shared/excel-upload-preview";
import {
  hasNonRemovableConflict,
  hasRemovableConflict,
  type CampaignConflictItem,
} from "../../../utils/conflict-resolution";

export interface Pro017StepFourData {
  lists: Pro017List[];
}

interface StepFourProps {
  data: Pro017StepFourData;
  updateData: (data: Partial<Pro017StepFourData>) => void;
  nextStep: () => void;
  prevStep: () => void;
  mode?: "create" | "edit";
  originalSkusPerList?: Record<number, Set<string>>;
  conflicts?: CampaignConflictItem[];
  conflictFilter?: "all" | "conflict" | "clean";
  onConflictFilterChange?: (filter: "all" | "conflict" | "clean") => void;
  /** Marca o item para remoção da(s) campanha(s) anterior(es) (aplicado ao criar). */
  onRemoveFromPrevious?: (conflict: CampaignConflictItem) => void;
}

export default function StepFour({
  data,
  updateData,
  nextStep,
  prevStep,
  mode = "create",
  originalSkusPerList,
  conflicts = [],
  conflictFilter = "all",
  onConflictFilterChange,
  onRemoveFromPrevious,
}: StepFourProps) {
  const isPendingSku = useCallback(
    (listNumber: number, sku: string): boolean => {
      if (mode !== "edit" || !originalSkusPerList) return false;
      const original = originalSkusPerList[listNumber];
      if (!original) return false;
      // Normaliza ambos os lados — originalSkusPerList já vem normalizado do
      // parent, mas o SKU do estado local pode ter vindo do Excel sem normalização.
      const normalized = String(sku).trim().toUpperCase();
      return !original.has(normalized);
    },
    [mode, originalSkusPerList],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentListNumber, setCurrentListNumber] = useState(1);
  const [itemCodeInput, setItemCodeInput] = useState("");
  const [searchTerms, setSearchTerms] = useState<Record<number, string>>({});
  const [pagesByList, setPagesByList] = useState<Record<number, number>>({});
  const pageSize = 50;
  const conflictByCode = new Map(conflicts.map((conflict) => [conflict.itemCode, conflict]));
  const conflictCodes = new Set(conflictByCode.keys());

  const isFormValid = useMemo(() => {
    return data.lists.every((list) => list.items.length >= 2);
  }, [data.lists]);

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Cada lista deve ter no mínimo 2 itens
    data.lists.forEach((list) => {
      if (list.items.length < 2) {
        newErrors[`list_${list.listNumber}`] = `Lista ${list.listNumber} deve ter no mínimo 2 produtos`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddItem = (listNumber: number) => {
    const trimmedCode = itemCodeInput.trim().toUpperCase();

    if (!trimmedCode) {
      setErrors({ ...errors, [`input_${listNumber}`]: "Digite um código de produto" });
      return;
    }

    const list = data.lists.find((l) => l.listNumber === listNumber);
    if (!list) return;

    // Não pode haver itens duplicados na mesma lista
    // Ignorar silenciosamente duplicatas
    if (list.items.includes(trimmedCode)) {
      setItemCodeInput("");
      return; // Silenciosamente ignora duplicata
    }

    const updatedLists = data.lists.map((l) =>
      l.listNumber === listNumber
        ? { ...l, items: [...l.items, trimmedCode] }
        : l
    );

    updateData({ lists: updatedLists });
    setItemCodeInput("");
    setErrors({ ...errors, [`input_${listNumber}`]: "", [`list_${listNumber}`]: "" });
  };

  const handleRemoveItem = (listNumber: number, itemCode: string) => {
    const updatedLists = data.lists.map((l) =>
      l.listNumber === listNumber
        ? { ...l, items: l.items.filter((item) => item !== itemCode) }
        : l
    );

    updateData({ lists: updatedLists });
    setPagesByList((pages) => ({ ...pages, [listNumber]: 1 }));
  };

  const handleRemoveConflictingItems = () => {
    updateData({
      lists: data.lists.map((list) => ({
        ...list,
        items: list.items.filter((sku) => !conflictCodes.has(sku)),
      })),
    });
    setPagesByList({});
  };

  const handleNext = () => {
    if (validateStep()) {
      nextStep();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, listNumber: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddItem(listNumber);
    }
  };

  // Story 1.5: Excel import per list
  const currentList = data.lists.find((l) => l.listNumber === currentListNumber);
  const currentListAsSkuItems = useMemo(
    () => (currentList?.items || []).map((sku) => ({ sku })),
    [currentList?.items],
  );

  const {
    fileInputRef,
    uploadProgress,
    isProcessing,
    preview,
    handleFileUpload,
    closePreview,
    confirmImport,
  } = useExcelImport(currentListAsSkuItems);

  const handleConfirmImport = useCallback(
    async (items: Array<{ sku: string; name?: string }>): Promise<void> => {
      await confirmImport(items, async (confirmed) => {
        const newSkus = confirmed.map((item) => item.sku.toUpperCase());
        const updatedLists = data.lists.map((l) =>
          l.listNumber === currentListNumber
            ? { ...l, items: [...l.items, ...newSkus] }
            : l,
        );
        updateData({ lists: updatedLists });
        toast({
          title: "Importação concluída",
          description: `${newSkus.length} SKU(s) adicionado(s) à Lista ${currentListNumber}`,
          variant: "default",
        });
      });
    },
    [confirmImport, data.lists, currentListNumber, updateData],
  );

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/templates/sku-import-template.xlsx";
    link.download = "sku-import-template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div className="bg-card rounded-lg border p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">
              Adicionar Produtos às Listas *
            </h3>
            <p className="text-sm text-muted-foreground">
              Adicione no mínimo 2 produtos para cada lista do combo
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="text-xs text-blue-900">
              <p className="font-semibold mb-1">Regras importantes:</p>
              <ul className="space-y-1">
                <li>• Cada lista deve ter no mínimo 2 produtos</li>
                <li>• Não pode haver produtos duplicados na mesma lista</li>
                <li>• Um mesmo produto pode estar em listas diferentes</li>
                <li>• Digite o código do produto e pressione Enter ou clique em Adicionar</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tabs for Lists */}
        <Tabs
          value={currentListNumber.toString()}
          onValueChange={(value) => {
            setCurrentListNumber(parseInt(value));
            setItemCodeInput("");
          }}
          className="w-full"
        >
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${data.lists.length}, minmax(0, 1fr))` }}>
            {data.lists.map((list) => (
              <TabsTrigger
                key={list.listNumber}
                value={list.listNumber.toString()}
                className="relative"
              >
                Lista {list.listNumber}
                {list.items.length >= 2 && (
                  <Badge
                    variant="default"
                    className="ml-2 h-5 w-5 p-0 flex items-center justify-center rounded-full"
                  >
                    <Check className="h-3 w-3" />
                  </Badge>
                )}
                {list.items.length > 0 && list.items.length < 2 && (
                  <Badge
                    variant="outline"
                    className="ml-2 h-5 w-5 p-0 flex items-center justify-center rounded-full text-yellow-600 border-yellow-600"
                  >
                    !
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {data.lists.map((list) => (
            <TabsContent
              key={list.listNumber}
              value={list.listNumber.toString()}
              className="space-y-4 mt-4"
            >
              {/* Add Item Input */}
              <div className="space-y-2">
                <Label htmlFor={`item-input-${list.listNumber}`} className="text-sm font-medium">
                  Adicionar Produto à Lista {list.listNumber}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id={`item-input-${list.listNumber}`}
                    value={itemCodeInput}
                    onChange={(e) => {
                      setItemCodeInput(e.target.value);
                      setErrors({ ...errors, [`input_${list.listNumber}`]: "" });
                    }}
                    onKeyPress={(e) => handleKeyPress(e, list.listNumber)}
                    placeholder="Ex: PROD001"
                    className="h-9 flex-1 uppercase"
                  />
                  <Button
                    type="button"
                    onClick={() => handleAddItem(list.listNumber)}
                    className="h-9"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar
                  </Button>
                </div>
                {errors[`input_${list.listNumber}`] && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors[`input_${list.listNumber}`]}
                  </p>
                )}
              </div>

              {/* Story 1.5: Excel Import Section */}
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-sm font-medium">
                  Importação em Massa via Excel
                </Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    className="h-9 flex-1"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Baixar Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessing}
                    className="h-9 flex-1"
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {uploadProgress !== null ? `Importando... ${uploadProgress}%` : "Importar Excel"}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
                {uploadProgress !== null && (
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-primary h-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <div className="flex items-start gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-900">
                      <strong>Dica:</strong> Importe SKUs em massa para a Lista {list.listNumber}. Duplicatas dentro da mesma lista são ignoradas. O mesmo SKU pode existir em listas diferentes.
                    </p>
                  </div>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-2">
                {(() => {
                  const searchTerm = searchTerms[list.listNumber] || "";
                  const filteredItems = list.items
                    .filter((itemCode) => {
                      const hasConflict = conflictCodes.has(itemCode);
                      if (conflictFilter === "conflict") return hasConflict;
                      if (conflictFilter === "clean") return !hasConflict;
                      return true;
                    })
                    .filter((itemCode) => itemCode.toLowerCase().includes(searchTerm.trim().toLowerCase()));
                  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
                  const currentPage = Math.min(pagesByList[list.listNumber] || 1, totalPages);
                  const paginatedItems = filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

                  return (
                    <>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="text-sm font-medium">
                    Produtos na Lista {list.listNumber}
                  </Label>
                  <div className="flex items-center gap-2">
                    {conflicts.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveConflictingItems}
                        className="h-7 text-destructive hover:text-destructive"
                      >
                        Remover todos com conflito
                      </Button>
                    )}
                    {(() => {
                      const pendingCount = list.items.filter((sku) =>
                        isPendingSku(list.listNumber, sku),
                      ).length;
                      return pendingCount > 0 ? (
                        <Badge
                          variant="outline"
                          className="border-amber-400 bg-amber-100 text-amber-800 text-xs"
                        >
                          +{pendingCount} novo(s)
                        </Badge>
                      ) : null;
                    })()}
                    <Badge variant={list.items.length >= 2 ? "default" : "outline"}>
                      {list.items.length} produto(s)
                    </Badge>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-2">
                  <Input
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerms((terms) => ({ ...terms, [list.listNumber]: event.target.value }));
                      setPagesByList((pages) => ({ ...pages, [list.listNumber]: 1 }));
                    }}
                    placeholder="Buscar SKU"
                    className="h-9 md:max-w-xs"
                  />
                  <Select
                    value={conflictFilter}
                    onValueChange={(value) => {
                      onConflictFilterChange?.(value as "all" | "conflict" | "clean");
                      setPagesByList((pages) => ({ ...pages, [list.listNumber]: 1 }));
                    }}
                  >
                    <SelectTrigger className="h-9 w-full md:w-44">
                      <SelectValue placeholder="Filtrar validade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="clean">Válidos</SelectItem>
                      <SelectItem value="conflict">Com conflito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {mode === "edit" &&
                  list.items.some((sku) => isPendingSku(list.listNumber, sku)) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-900">
                        Itens destacados em <strong>amarelo</strong> ainda não foram salvos. Clique em
                        <strong> Salvar </strong>no último passo para persistir.
                      </p>
                    </div>
                  )}

                {list.items.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhum produto adicionado ainda</p>
                    <p className="text-xs">Adicione no mínimo 2 produtos para continuar</p>
                  </div>
                ) : (
                  <div className="grid gap-2 max-h-64 overflow-y-auto p-1">
                    {paginatedItems.map((itemCode, index) => {
                      const pending = isPendingSku(list.listNumber, itemCode);
                      const hasConflict = conflictCodes.has(itemCode);
                      return (
                        <div
                          key={`${itemCode}-${index}`}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            hasConflict
                              ? "border-red-300 bg-red-50 hover:border-red-500"
                              : pending
                              ? "border-amber-400 bg-amber-50 hover:border-amber-500"
                              : "border-border bg-card hover:border-primary/50"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                pending ? "bg-amber-200" : "bg-primary/10"
                              }`}
                            >
                                {hasConflict ? (
                                  <AlertCircle className="h-4 w-4 text-red-700" />
                                ) : pending ? (
                                  <Plus className="h-4 w-4 text-amber-700" />
                                ) : (
                                <span className="text-xs font-bold text-primary">
                                  {(currentPage - 1) * pageSize + index + 1}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{itemCode}</p>
                                {pending && (
                                  <Badge
                                    variant="outline"
                                    className="border-amber-400 bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0 h-4 leading-none"
                                  >
                                    NOVO
                                  </Badge>
                                )}
                                {hasConflict && (
                                  <Badge
                                    variant="outline"
                                    className="border-red-400 bg-red-100 text-red-800 text-[10px] px-1.5 py-0 h-4 leading-none"
                                  >
                                    CONFLITO
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {pending
                                  ? "Será salvo ao clicar em Salvar"
                                  : "Código do Produto"}
                              </p>
                            </div>
                          </div>
                          {(() => {
                            const conflict = conflictByCode.get(itemCode);
                            const removable = conflict ? hasRemovableConflict(conflict) : false;
                            const onlyNonRemovable =
                              conflict && !removable && hasNonRemovableConflict(conflict);

                            return (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {conflict && onRemoveFromPrevious && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-8 gap-1"
                                    disabled={!removable}
                                    title={
                                      onlyNonRemovable
                                        ? "Campanhas por departamento/grupo (PRO013) não permitem remover um único produto."
                                        : "Marcar para remover este produto da campanha anterior ao criar"
                                    }
                                    onClick={() => onRemoveFromPrevious(conflict)}
                                  >
                                    <ArrowLeftRight className="h-3.5 w-3.5" />
                                    Remover da anterior
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRemoveItem(list.listNumber, itemCode)}
                                  title="Descartar item desta campanha"
                                  className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground border-destructive/20 text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                    {filteredItems.length === 0 && (
                      <div className="text-center py-8 border rounded-lg bg-card text-muted-foreground">
                        <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nenhum produto encontrado</p>
                      </div>
                    )}
                  </div>
                )}

                {list.items.length > 0 && filteredItems.length > 0 && (
                  <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      Mostrando {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, filteredItems.length)} de {filteredItems.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={currentPage <= 1}
                        onClick={() =>
                          setPagesByList((pages) => ({
                            ...pages,
                            [list.listNumber]: Math.max(1, currentPage - 1),
                          }))
                        }
                      >
                        Anterior
                      </Button>
                      <span>
                        Página {currentPage} de {totalPages}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={currentPage >= totalPages}
                        onClick={() =>
                          setPagesByList((pages) => ({
                            ...pages,
                            [list.listNumber]: Math.min(totalPages, currentPage + 1),
                          }))
                        }
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                )}

                    </>
                  );
                })()}

                {errors[`list_${list.listNumber}`] && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors[`list_${list.listNumber}`]}
                  </p>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Summary */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Total de Listas</p>
              <p className="text-lg font-bold text-primary">{data.lists.length}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total de Produtos</p>
              <p className="text-lg font-bold text-primary">
                {data.lists.reduce((sum, list) => sum + list.items.length, 0)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Listas Completas</p>
              <p className="text-lg font-bold text-primary">
                {data.lists.filter((list) => list.items.length >= 2).length}/{data.lists.length}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Combinações</p>
              <p className="text-lg font-bold text-primary">
                {data.lists.every((list) => list.items.length > 0)
                  ? data.lists.reduce((product, list) => product * list.items.length, 1)
                  : 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          onClick={prevStep}
          className="w-full md:w-auto"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isFormValid}
          className="w-full md:w-auto"
        >
          Continuar
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {preview && (
        <ExcelUploadPreview
          isOpen={!!preview}
          onClose={closePreview}
          onConfirm={handleConfirmImport}
          validItems={preview.validItems}
          invalidSkus={preview.invalidSkus}
          duplicateSkus={preview.duplicateSkus}
        />
      )}
    </div>
  );
}
